/**
 * Billing & Subscription Page
 *
 * Manage subscription tiers and billing settings.
 */

import { useCallback, useEffect, useState, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { api, setAuthToken } from '../api';
import { useAuth } from '../hooks/useAuth';
import { supabase } from '../lib/supabase';
import SubscriptionTierCards, { type SubscriptionTier } from '../components/SubscriptionTierCards';
import BillingPeriodToggle, { type BillingPeriod } from '../components/BillingPeriodToggle';
import SubscriptionBlockedModal from '../components/SubscriptionBlockedModal';
import '../styles/BillingPage.css';

export default function BillingPage() {
  const { profile, refreshProfile, user, session } = useAuth();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  // processingTier state kept for when subscriptions are re-enabled
  const [processingTier, _setProcessingTier] = useState<string | null>(null);
  const [portalLoading, setPortalLoading] = useState(false);
  const [syncLoading, setSyncLoading] = useState(false);
  const [statusMessage, setStatusMessage] = useState<string | null>(null);
  const [statusType, setStatusType] = useState<'success' | 'error' | 'info' | 'warning' | null>(null);
  const checkingSubscription = useRef(false);
  const [billingPeriod, setBillingPeriod] = useState<BillingPeriod>('monthly');
  const [showBlockedModal, setShowBlockedModal] = useState(false);

  // Local state for subscription info (overrides profile if set)
  const [localTier, setLocalTier] = useState<string | null>(null);
  const [localStatus, setLocalStatus] = useState<string | null>(null);
  const [tierMismatch, setTierMismatch] = useState(false);

  const setAlert = useCallback((type: 'success' | 'error' | 'info' | 'warning', message: string) => {
    setStatusType(type);
    setStatusMessage(message);
  }, []);

  const clearAlert = useCallback(() => {
    setStatusMessage(null);
    setStatusType(null);
  }, []);

  const getAuthToken = useCallback(async () => {
    if (session?.access_token) {
      console.log('[BillingPage] Using in-memory session token for sync');
      return session.access_token;
    }

    const {
      data: { session: cachedSession },
      error: sessionError,
    } = await supabase.auth.getSession();

    if (sessionError) {
      console.error('[BillingPage] Failed to get cached session for auth token:', sessionError);
    }

    if (cachedSession?.access_token) {
      console.log('[BillingPage] Retrieved cached session token for sync');
      return cachedSession.access_token;
    }

    const { data: refreshed, error: refreshError } = await supabase.auth.refreshSession();

    if (refreshError) {
      console.error('[BillingPage] Failed to refresh session for auth token:', refreshError);
    } else {
      console.log('[BillingPage] Refreshed session before sync');
    }

    if (refreshed.session?.access_token) {
      return refreshed.session.access_token;
    }

    throw new Error('Please sign in again before syncing your subscription.');
  }, [session]);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        setLoading(true);
        // Verify API is accessible
        await api.getSubscriptionTiers();
      } catch (error) {
        console.error('Failed to load subscription tiers', error);
        if (mounted) {
          setAlert('error', 'Unable to load subscription plans.');
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    })();

    return () => {
      mounted = false;
    };
  }, [setAlert]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const status = params.get('status');

    if (status === 'success' && !checkingSubscription.current) {
      checkingSubscription.current = true;
      setAlert('success', 'Subscription updated successfully! Syncing your account...');

      // Automatically sync subscription from Stripe
      const syncAndRefresh = async () => {
        try {
          const token = await getAuthToken();
          console.log('[BillingPage] Acquired auth token for automatic sync');
          setAuthToken(token);
          const result = await api.syncSubscription();
          console.log('[BillingPage] Auto-sync result:', result);

          // Update local state with synced data
          if (result.updated) {
            setLocalTier(result.updated.subscription_tier);
            setLocalStatus(result.updated.subscription_status);
          } else if (result.subscription) {
            setLocalTier(result.subscription.tier);
            setLocalStatus(result.subscription.status);
          }

          // Force refresh the profile to update auth context
          await refreshProfile({ force: true, silent: false });

          setAlert('success', 'Subscription activated! Your account has been upgraded.');
          checkingSubscription.current = false;
        } catch (error) {
          console.error('[BillingPage] Auto-sync failed:', error);

          // Fallback to polling if sync fails
          setAlert('info', 'Syncing subscription... This may take a moment.');
          let attempts = 0;
          const maxAttempts = 8;
          const intervalMs = 2000;

          const checkSubscription = async () => {
            attempts++;
            await refreshProfile({ force: true, silent: false }).catch(console.error);

            setTimeout(() => {
              const currentTier = profile?.subscription_tier || 'free';

              if (currentTier === 'free' && attempts < maxAttempts) {
                setTimeout(checkSubscription, intervalMs);
              } else if (currentTier !== 'free') {
                setAlert('success', 'Subscription updated successfully!');
                checkingSubscription.current = false;
              } else if (attempts >= maxAttempts) {
                setAlert('info', 'Subscription is processing. Please click the Sync button or refresh the page.');
                checkingSubscription.current = false;
              }
            }, 500);
          };

          checkSubscription();
        }
      };

      syncAndRefresh();
    } else if (status === 'canceled') {
      setAlert('info', 'Subscription update canceled.');
    }

    if (status) {
      params.delete('status');
      const nextQuery = params.toString();
      const nextUrl = nextQuery ? `${location.pathname}?${nextQuery}` : location.pathname;
      window.history.replaceState({}, document.title, nextUrl);
    }
  }, [location.pathname, location.search, refreshProfile, setAlert, profile?.subscription_tier, getAuthToken]);

  const handleSubscribe = async (_tierId: 'gold' | 'platinum') => {
    // Temporarily block new subscriptions
    setShowBlockedModal(true);
  };

  const handleManageSubscription = async () => {
    try {
      setPortalLoading(true);
      clearAlert();
      const { url } = await api.createPortalSession(`${window.location.origin}/account/billing`);
      window.location.assign(url);
    } catch (error) {
      console.error('Failed to create portal session:', error);
      setAlert('error', 'Failed to load billing portal. Please try again.');
      setPortalLoading(false);
    }
  };

  const handleSyncSubscription = async () => {
    try {
      setSyncLoading(true);
      clearAlert();

      console.log('[BillingPage] Profile before sync:', profile);
      console.log('[BillingPage] User:', user);

      const token = await getAuthToken();
      console.log('[BillingPage] Acquired auth token for manual sync');
      setAuthToken(token);

      const result = await api.syncSubscription();
      console.log('[BillingPage] Sync result:', result);

      // Use the updated profile data from the sync response
      if (result.updated) {
        console.log('[BillingPage] Updating local tier from sync response:', result.updated);
        setLocalTier(result.updated.subscription_tier);
        setLocalStatus(result.updated.subscription_status);
        console.log('[BillingPage] Local state updated to:', result.updated.subscription_tier);

        // Force clear cached profile to ensure fresh fetch
        console.log('[BillingPage] Clearing profile cache to force refresh');
        sessionStorage.removeItem('cached_profile');
        sessionStorage.removeItem('cached_profile_time');

        // Force refresh the AuthContext profile
        console.log('[BillingPage] Forcing profile refresh in AuthContext');
        await refreshProfile({ force: true, silent: false });

        // Wait a brief moment for state to propagate
        await new Promise(resolve => setTimeout(resolve, 500));

        // Verify the sync actually propagated to global state
        const updatedTier = profile?.subscription_tier;
        console.log('[BillingPage] Verifying tier update:', {
          syncedTier: result.updated.subscription_tier,
          globalProfileTier: updatedTier
        });

        if (updatedTier !== result.updated.subscription_tier) {
          console.warn('[BillingPage] Profile refresh did not fully propagate tier update');
          setTierMismatch(true);
          setAlert('warning',
            `Subscription synced to ${result.updated.subscription_tier.toUpperCase()}, but may require a page refresh to fully update across the site.`
          );
        } else {
          setTierMismatch(false);
          setAlert('success', result.message);
        }
      } else {
        setAlert('success', result.message);
      }
    } catch (error) {
      console.error('Failed to sync subscription:', error);
      const message = error instanceof Error ? error.message : 'Failed to sync subscription';
      setAlert('error', message);
    } finally {
      setSyncLoading(false);
    }
  };

  // Use local state if available (after sync), otherwise fall back to profile
  const currentTier = (localTier || profile?.subscription_tier || 'free') as SubscriptionTier;
  const isTrialing = (localStatus || profile?.subscription_status) === 'trialing';

  // Detect tier mismatch between local and global state
  useEffect(() => {
    if (localTier && profile?.subscription_tier && localTier !== profile.subscription_tier) {
      console.log('[BillingPage] Tier mismatch detected:', {
        localTier,
        profileTier: profile.subscription_tier
      });
      setTierMismatch(true);
    } else {
      setTierMismatch(false);
    }
  }, [localTier, profile?.subscription_tier]);

  const renderBillingCTA = (tier: SubscriptionTier) => {
    const baseClass = `subscription-cta subscription-cta-${tier}`;
    const isCurrentTier = currentTier === tier;

    if (isCurrentTier) {
      if (tier === 'free') {
        return (
          <button type="button" className={baseClass} disabled>
            Current Plan
          </button>
        );
      }

      return (
        <button
          type="button"
          className={baseClass}
          onClick={handleManageSubscription}
          disabled={portalLoading}
        >
          {portalLoading ? 'Opening Portal...' : 'Manage Subscription'}
        </button>
      );
    }

    if (tier === 'free') {
      return (
        <button
          type="button"
          className={baseClass}
          onClick={handleManageSubscription}
          disabled={portalLoading}
        >
          Downgrade
        </button>
      );
    }

    if (tier === 'gold') {
      if (currentTier === 'platinum') {
        return (
          <button
            type="button"
            className={baseClass}
            onClick={handleManageSubscription}
            disabled={portalLoading}
          >
            Downgrade
          </button>
        );
      }

      return (
        <button
          type="button"
          className={baseClass}
          onClick={() => handleSubscribe('gold')}
          disabled={processingTier === 'gold'}
        >
          {processingTier === 'gold' ? 'Processing...' : 'Upgrade to Gold'}
        </button>
      );
    }

    return (
      <button
        type="button"
        className={baseClass}
        onClick={() => handleSubscribe('platinum')}
        disabled={processingTier === 'platinum'}
      >
        {processingTier === 'platinum' ? 'Processing...' : 'Upgrade to Platinum'}
      </button>
    );
  };


  if (loading) {
    return (
      <div className="billing-page loading">
        <div className="spinner"></div>
        <p>Loading plans...</p>
      </div>
    );
  }

  return (
    <div className="billing-page">
      <div className="billing-container">
        <div className="billing-header">
          <div>
            <h1 className="billing-title">Subscription Plans</h1>
            <p className="billing-subtitle">
              Choose the plan that fits your betting strategy
            </p>
          </div>
          <Link to="/account" className="back-link">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M19 12H5M12 19l-7-7 7-7" />
            </svg>
            Back to Account
          </Link>
        </div>

        {statusMessage && (
          <div className={`billing-alert billing-alert--${statusType || 'info'}`}>
            <span>{statusMessage}</span>
            {statusMessage.includes('Refresh Now') ? (
              <button
                type="button"
                onClick={() => window.location.reload()}
                style={{
                  background: 'var(--primary)',
                  color: 'white',
                  border: 'none',
                  padding: '4px 12px',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontWeight: 600
                }}
              >
                Refresh Now
              </button>
            ) : (
              <button type="button" onClick={clearAlert} aria-label="Dismiss message">
                &times;
              </button>
            )}
          </div>
        )}

        {tierMismatch && (
          <div className="billing-alert billing-alert--warning">
            <span>⚠️ Subscription tier is updating. Please refresh the page to see changes across the entire site.</span>
            <button
              type="button"
              onClick={() => window.location.reload()}
              style={{
                background: 'var(--primary)',
                color: 'white',
                border: 'none',
                padding: '4px 12px',
                borderRadius: '4px',
                cursor: 'pointer',
                fontWeight: 600
              }}
            >
              Refresh Now
            </button>
          </div>
        )}

        <div className="current-plan-banner">
          <div className="plan-info">
            <span className="plan-label">Current Plan</span>
            <span className="plan-name">
              {currentTier.charAt(0).toUpperCase() + currentTier.slice(1)}
              {isTrialing && ' (Trial)'}
            </span>
          </div>
          <div style={{ display: 'flex', gap: '12px' }}>
            <button
              className="btn btn-outline"
              onClick={handleSyncSubscription}
              disabled={syncLoading}
              title="Sync subscription status from Stripe"
            >
              {syncLoading ? 'Syncing...' : '🔄 Sync'}
            </button>
            {currentTier !== 'free' && (
              <button
                className="btn btn-outline"
                onClick={handleManageSubscription}
                disabled={portalLoading}
              >
                {portalLoading ? 'Loading...' : 'Manage Subscription'}
              </button>
            )}
          </div>
        </div>

        <BillingPeriodToggle
          selected={billingPeriod}
          onChange={setBillingPeriod}
        />

        <SubscriptionTierCards
          renderCTA={renderBillingCTA}
          billingPeriod={billingPeriod}
        />
      </div>

      <SubscriptionBlockedModal
        isOpen={showBlockedModal}
        onClose={() => setShowBlockedModal(false)}
      />
    </div>
  );
}
